import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Headphones,
  Upload,
  X,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  ShieldCheck,
  Smartphone,
  Link2,
  Mic,
  FileCheck,
  Download,
  Square,
  RotateCcw,
  ExternalLink,
} from 'lucide-react';
import { FEATURE_DIKTAT } from '../../../constants';
import type { DiktatTab } from '../portalNavigation';
import { PORTAL_SHELL } from '../portalTheme';
import {
  buildElementRoomUrl,
  ELEMENT_DOWNLOAD_URL,
} from './MatrixView';

export interface DiktatViewProps {
  activeTab: DiktatTab;
  onTabChange: (tab: DiktatTab) => void;
  onOpenChat: () => void;
  diktatFile: File | null;
  onDiktatFileChange: (file: File | null) => void;
  diktatUploading: boolean;
  onDiktatUpload: () => void;
  diktatResult: {
    type: 'success' | 'error';
    pdfUrl?: string;
    message?: string;
    pb_record_id?: string;
  } | null;
  onDiktatResultClear: () => void;
  onShowInDokumente?: (pbRecordId?: string) => void;
  /** PB-Feld matrixRoomId — Raum-Link im Reiter Handy/Element. */
  matrixRoomId?: string;
}

const DIKTAT_TABS: Array<{ id: DiktatTab; label: string }> = [
  { id: 'record', label: 'Aufnehmen' },
  { id: 'upload', label: 'Hochladen' },
  { id: 'anleitung', label: 'Handy / Element' },
];

function pickRecorderMimeType(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm', 'audio/mp4', ''];
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  for (const c of candidates) {
    if (c === '' || MediaRecorder.isTypeSupported(c)) return c;
  }
  return '';
}

function extensionForMime(mime: string): string {
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('mp4') || mime.includes('m4a') || mime.includes('aac')) return 'm4a';
  return 'webm';
}

function formatDuration(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

export const DiktatView: React.FC<DiktatViewProps> = ({
  activeTab,
  onTabChange,
  onOpenChat,
  diktatFile,
  onDiktatFileChange,
  diktatUploading,
  onDiktatUpload,
  diktatResult,
  onDiktatResultClear,
  onShowInDokumente,
  matrixRoomId = '',
}) => (
  <div className="space-y-6 animate-fade-in max-w-2xl mx-auto">
    <header>
      <h1 className="text-xl font-semibold text-white tracking-tight">{FEATURE_DIKTAT}</h1>
      <p className={`${PORTAL_SHELL.textMuted} text-sm mt-1`}>
        Aufnehmen, Datei hochladen oder per Handy/Element senden — Transkription &amp; Protokoll folgen.
      </p>
    </header>

    <div className="flex gap-1 p-1 rounded-lg bg-white/[0.04] border border-white/[0.06] w-full sm:w-fit">
      {DIKTAT_TABS.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onTabChange(tab.id)}
          className={`flex-1 sm:flex-none px-3 sm:px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
            activeTab === tab.id
              ? 'bg-white/[0.08] text-white'
              : 'text-slate-500 hover:text-slate-300'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>

    {activeTab === 'record' && (
      <DiktatRecordPanel
        diktatFile={diktatFile}
        onDiktatFileChange={onDiktatFileChange}
        diktatUploading={diktatUploading}
        onDiktatUpload={onDiktatUpload}
        diktatResult={diktatResult}
        onDiktatResultClear={onDiktatResultClear}
        onShowInDokumente={onShowInDokumente}
        onShowUpload={() => onTabChange('upload')}
        onShowAnleitung={() => onTabChange('anleitung')}
      />
    )}

    {activeTab === 'upload' && (
      <DiktatUploadPanel
        diktatFile={diktatFile}
        onDiktatFileChange={onDiktatFileChange}
        diktatUploading={diktatUploading}
        onDiktatUpload={onDiktatUpload}
        diktatResult={diktatResult}
        onDiktatResultClear={onDiktatResultClear}
        onShowInDokumente={onShowInDokumente}
        onShowAnleitung={() => onTabChange('anleitung')}
      />
    )}

    {activeTab === 'anleitung' && (
      <DiktatAnleitungPanel onOpenChat={onOpenChat} matrixRoomId={matrixRoomId} />
    )}
  </div>
);

const DiktatResultBlocks: React.FC<{
  diktatResult: DiktatViewProps['diktatResult'];
  onDiktatResultClear: () => void;
  onShowInDokumente?: (pbRecordId?: string) => void;
}> = ({ diktatResult, onDiktatResultClear, onShowInDokumente }) => (
  <>
    {diktatResult?.type === 'success' && (
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5 space-y-3">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-emerald-400 font-semibold text-sm">Diktat übermittelt</p>
            {diktatResult.message && <p className="text-slate-300 text-sm mt-1">{diktatResult.message}</p>}
            {!diktatResult.pdfUrl && (
              <p className="text-slate-400 text-sm mt-1">Das Protokoll erscheint unter Dokumente.</p>
            )}
          </div>
        </div>
        {diktatResult.pdfUrl && (
          <a
            href={diktatResult.pdfUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 w-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold py-2.5 rounded-xl text-sm"
          >
            <Download className="w-4 h-4" /> Protokoll-PDF
          </a>
        )}
        {onShowInDokumente && (
          <button
            type="button"
            onClick={() => onShowInDokumente(diktatResult.pb_record_id)}
            className="flex items-center justify-center gap-2 w-full bg-violet-600/80 hover:bg-violet-600 text-white font-semibold py-2.5 rounded-xl text-sm border border-violet-500/40"
          >
            <FileCheck className="w-4 h-4" /> In Dokumente anzeigen
          </button>
        )}
        <button
          type="button"
          onClick={onDiktatResultClear}
          className="w-full text-slate-400 hover:text-white text-sm py-1"
        >
          Weiteres Diktat
        </button>
      </div>
    )}

    {diktatResult?.type === 'error' && (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
        <div>
          <p className="text-red-400 font-semibold text-sm">Upload fehlgeschlagen</p>
          <p className="text-slate-400 text-sm mt-1">
            Bitte erneut versuchen oder{' '}
            <a href="mailto:office@arkorbit.at" className="text-blue-400 underline">
              office@arkorbit.at
            </a>
          </p>
        </div>
      </div>
    )}
  </>
);

const DiktatUploadAction: React.FC<{
  diktatFile: File | null;
  diktatUploading: boolean;
  onDiktatUpload: () => void;
}> = ({ diktatFile, diktatUploading, onDiktatUpload }) => (
  <button
    type="button"
    onClick={onDiktatUpload}
    disabled={!diktatFile || diktatUploading}
    className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-800 disabled:text-slate-600 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 text-sm"
  >
    {diktatUploading ? (
      <>
        <Loader2 className="w-4 h-4 animate-spin" /> Wird verarbeitet…
      </>
    ) : (
      <>
        <Upload className="w-4 h-4" /> Diktat übermitteln
      </>
    )}
  </button>
);

const PrivacyHint: React.FC = () => (
  <div className="flex items-start gap-2 text-xs text-slate-500 leading-relaxed">
    <ShieldCheck className="w-4 h-4 text-emerald-500/80 flex-shrink-0 mt-0.5" />
    Verarbeitung in Frankfurt (EU). Transkription ohne Drittanbieter-Cloud.
  </div>
);

type RecordPhase = 'idle' | 'recording' | 'preview';

const DiktatRecordPanel: React.FC<{
  diktatFile: File | null;
  onDiktatFileChange: (file: File | null) => void;
  diktatUploading: boolean;
  onDiktatUpload: () => void;
  diktatResult: DiktatViewProps['diktatResult'];
  onDiktatResultClear: () => void;
  onShowInDokumente?: (pbRecordId?: string) => void;
  onShowUpload: () => void;
  onShowAnleitung: () => void;
}> = ({
  diktatFile,
  onDiktatFileChange,
  diktatUploading,
  onDiktatUpload,
  diktatResult,
  onDiktatResultClear,
  onShowInDokumente,
  onShowUpload,
  onShowAnleitung,
}) => {
  const [phase, setPhase] = useState<RecordPhase>(diktatFile ? 'preview' : 'idle');
  const [elapsedSec, setElapsedSec] = useState(0);
  const [micError, setMicError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const timerRef = useRef<number | null>(null);
  const mimeRef = useRef('');

  const stopTracks = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const revokePreview = useCallback(() => {
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const resetRecording = useCallback(() => {
    clearTimer();
    stopTracks();
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try {
        rec.stop();
      } catch {
        /* ignore */
      }
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    setElapsedSec(0);
    setMicError(null);
    revokePreview();
    onDiktatFileChange(null);
    setPhase('idle');
  }, [clearTimer, stopTracks, revokePreview, onDiktatFileChange]);

  useEffect(() => {
    if (!diktatFile) {
      if (phase === 'preview') {
        revokePreview();
        setPhase('idle');
      }
      return;
    }
    if (phase === 'recording') return;
    const url = URL.createObjectURL(diktatFile);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
    setPhase('preview');
    // Nur bei File-Wechsel; phase absichtlich nicht in deps (Recording-Stop setzt File).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diktatFile]);

  useEffect(
    () => () => {
      clearTimer();
      const rec = mediaRecorderRef.current;
      if (rec && rec.state !== 'inactive') {
        try {
          rec.ondataavailable = null;
          rec.onstop = null;
          rec.onerror = null;
          rec.stop();
        } catch {
          /* ignore */
        }
      }
      mediaRecorderRef.current = null;
      stopTracks();
      revokePreview();
    },
    [clearTimer, stopTracks, revokePreview],
  );

  const startRecording = useCallback(async () => {
    setMicError(null);
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setMicError('Mikrofon im Browser nicht verfügbar (HTTPS oder localhost nötig).');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
        video: false,
      });
      streamRef.current = stream;
      const mime = pickRecorderMimeType();
      mimeRef.current = mime;
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onerror = () => {
        setMicError('Aufnahme-Fehler. Bitte erneut versuchen.');
        resetRecording();
      };
      recorder.onstop = () => {
        stopTracks();
        clearTimer();
        const blobMime = mimeRef.current || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: blobMime });
        chunksRef.current = [];
        if (blob.size === 0) {
          setMicError('Keine Audiodaten aufgenommen.');
          setPhase('idle');
          return;
        }
        const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        const file = new File([blob], `diktat-${stamp}.${extensionForMime(blobMime)}`, {
          type: blobMime,
        });
        onDiktatFileChange(file);
        setPhase('preview');
      };
      mediaRecorderRef.current = recorder;
      recorder.start(250);
      setElapsedSec(0);
      setPhase('recording');
      timerRef.current = window.setInterval(() => {
        setElapsedSec((s) => s + 1);
      }, 1000);
    } catch {
      stopTracks();
      setMicError('Mikrofon-Zugriff verweigert oder nicht vorhanden.');
      setPhase('idle');
    }
  }, [onDiktatFileChange, resetRecording, stopTracks, clearTimer]);

  const stopRecording = useCallback(() => {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state === 'inactive') return;
    try {
      rec.stop();
    } catch {
      setMicError('Stop fehlgeschlagen.');
      resetRecording();
    }
  }, [resetRecording]);

  return (
    <div className="space-y-5">
      <DiktatResultBlocks
        diktatResult={diktatResult}
        onDiktatResultClear={onDiktatResultClear}
        onShowInDokumente={onShowInDokumente}
      />

      {!diktatResult && (
        <div className={`${PORTAL_SHELL.ingestBar} p-6 space-y-5`}>
          <div className="text-center space-y-1">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Mikrofon</p>
            <p className="text-slate-300 text-sm">Direkt im Browser aufnehmen — gleiche Pipeline wie Datei-Upload.</p>
          </div>

          {micError && (
            <div className="flex items-start gap-2 text-sm text-amber-300/90 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{micError}</span>
            </div>
          )}

          {phase === 'idle' && (
            <div className="flex flex-col items-center gap-4 py-4">
              <button
                type="button"
                onClick={() => void startRecording()}
                disabled={diktatUploading}
                className="w-20 h-20 rounded-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-800 text-white flex items-center justify-center shadow-lg shadow-emerald-900/30 transition-colors"
                aria-label="Aufnahme starten"
              >
                <Mic className="w-8 h-8" />
              </button>
              <p className="text-slate-400 text-sm">Tippen zum Aufnehmen</p>
            </div>
          )}

          {phase === 'recording' && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="relative">
                <span className="absolute inset-0 rounded-full bg-red-500/30 animate-ping" aria-hidden />
                <button
                  type="button"
                  onClick={stopRecording}
                  className="relative w-20 h-20 rounded-full bg-red-600 hover:bg-red-500 text-white flex items-center justify-center"
                  aria-label="Aufnahme stoppen"
                >
                  <Square className="w-7 h-7 fill-current" />
                </button>
              </div>
              <p className="text-red-300 text-sm font-medium tabular-nums">
                Aufnahme läuft · {formatDuration(elapsedSec)}
              </p>
              <p className="text-slate-500 text-xs">Stop beendet die Aufnahme und zeigt die Vorschau.</p>
            </div>
          )}

          {phase === 'preview' && diktatFile && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <Headphones className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-medium truncate">{diktatFile.name}</p>
                  <p className="text-slate-500 text-xs">
                    {(diktatFile.size / 1024).toFixed(0)} KB
                    {elapsedSec > 0 ? ` · ${formatDuration(elapsedSec)}` : ''}
                  </p>
                </div>
              </div>
              {previewUrl && (
                <audio controls src={previewUrl} className="w-full" preload="metadata">
                  Browser unterstützt keine Audio-Vorschau.
                </audio>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={resetRecording}
                  disabled={diktatUploading}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-white/[0.1] text-slate-300 hover:text-white hover:border-white/[0.2] text-sm font-medium"
                >
                  <RotateCcw className="w-4 h-4" /> Neu aufnehmen
                </button>
                <div className="flex-1">
                  <DiktatUploadAction
                    diktatFile={diktatFile}
                    diktatUploading={diktatUploading}
                    onDiktatUpload={onDiktatUpload}
                  />
                </div>
              </div>
            </div>
          )}

          <PrivacyHint />
        </div>
      )}

      <p className="text-slate-600 text-xs">
        Alternativ:{' '}
        <button type="button" onClick={onShowUpload} className="text-blue-400 hover:text-blue-300 underline">
          Datei hochladen
        </button>
        {' · '}
        <button type="button" onClick={onShowAnleitung} className="text-blue-400 hover:text-blue-300 underline">
          Handy / Element
        </button>
      </p>
    </div>
  );
};

const DiktatUploadPanel: React.FC<{
  diktatFile: File | null;
  onDiktatFileChange: (file: File | null) => void;
  diktatUploading: boolean;
  onDiktatUpload: () => void;
  diktatResult: DiktatViewProps['diktatResult'];
  onDiktatResultClear: () => void;
  onShowInDokumente?: (pbRecordId?: string) => void;
  onShowAnleitung: () => void;
}> = ({
  diktatFile,
  onDiktatFileChange,
  diktatUploading,
  onDiktatUpload,
  diktatResult,
  onDiktatResultClear,
  onShowInDokumente,
  onShowAnleitung,
}) => (
  <div className="space-y-5">
    <p className={`${PORTAL_SHELL.textMuted} text-sm`}>
      Audio-Datei wählen (MP3, M4A, WAV, WebM …) — Transkription &amp; Protokoll.
    </p>

    <DiktatResultBlocks
      diktatResult={diktatResult}
      onDiktatResultClear={onDiktatResultClear}
      onShowInDokumente={onShowInDokumente}
    />

    {!diktatResult && (
      <div className={`${PORTAL_SHELL.ingestBar} p-5 space-y-5`}>
        <label className="block">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">
            Audio-Datei
          </span>
          <label
            className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 cursor-pointer transition-all ${
              diktatFile ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-white/[0.08] hover:border-white/[0.15]'
            }`}
          >
            <input
              type="file"
              accept="audio/*,.m4a,.mp3,.wav,.ogg,.webm,.aac,.opus"
              className="hidden"
              onChange={(e) => onDiktatFileChange(e.target.files?.[0] || null)}
            />
            {diktatFile ? (
              <div className="text-center">
                <Headphones className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-white font-medium text-sm">{diktatFile.name}</p>
                <p className="text-slate-500 text-xs mt-1">{(diktatFile.size / 1024 / 1024).toFixed(1)} MB</p>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    onDiktatFileChange(null);
                  }}
                  className="mt-2 text-xs text-red-400 hover:text-red-300 flex items-center gap-1 mx-auto"
                >
                  <X className="w-3 h-3" /> Entfernen
                </button>
              </div>
            ) : (
              <div className="text-center">
                <Headphones className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                <p className="text-slate-300 text-sm">Datei ablegen oder wählen</p>
                <p className="text-slate-600 text-xs mt-1">MP3, M4A, WAV · max. 100 MB</p>
              </div>
            )}
          </label>
        </label>

        <DiktatUploadAction
          diktatFile={diktatFile}
          diktatUploading={diktatUploading}
          onDiktatUpload={onDiktatUpload}
        />

        <PrivacyHint />
      </div>
    )}

    <p className="text-slate-600 text-xs">
      Alternativ:{' '}
      <button type="button" onClick={onShowAnleitung} className="text-blue-400 hover:text-blue-300 underline">
        Handy / Element
      </button>{' '}
      — Ende-zu-Ende verschlüsselt.
    </p>
  </div>
);

const DiktatAnleitungPanel: React.FC<{
  onOpenChat: () => void;
  matrixRoomId?: string;
}> = ({ onOpenChat, matrixRoomId = '' }) => {
  const roomUrl = buildElementRoomUrl(matrixRoomId);

  return (
    <div className="space-y-4">
      {roomUrl ? (
        <div className={`${PORTAL_SHELL.ingestBar} p-4 space-y-3`}>
          <div className="flex gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <ExternalLink className="w-4 h-4 text-emerald-400" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-white text-sm font-medium">Kanzlei-Raum öffnen</h3>
              <p className="text-slate-400 text-sm mt-1 leading-relaxed">
                Sprachnachricht im Raum senden — Transkription und Protokoll folgen automatisch.
              </p>
            </div>
          </div>
          <a
            href={roomUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium"
          >
            <ExternalLink className="w-4 h-4" /> Im Browser öffnen (Element)
          </a>
        </div>
      ) : null}

      {[
        {
          step: 1,
          Icon: Smartphone,
          title: 'Element App installieren',
          body: (
            <div className="flex flex-col sm:flex-row gap-4 mt-2">
              <div className="bg-white rounded-lg p-2 shrink-0">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(ELEMENT_DOWNLOAD_URL)}`}
                  alt="QR: Element herunterladen"
                  className="w-[140px] h-[140px]"
                />
              </div>
              <div className="text-sm text-slate-400">
                <a
                  href={ELEMENT_DOWNLOAD_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300"
                >
                  <Link2 className="w-4 h-4" /> element.io/download
                </a>
                <p className="mt-2 text-slate-500 text-xs leading-relaxed">
                  Homeserver: <span className="text-slate-400">matrix.arkorbit.at</span>
                </p>
              </div>
            </div>
          ),
        },
        {
          step: 2,
          Icon: Link2,
          title: 'Kanzlei-Raum beitreten',
          body: (
            <p className="text-slate-400 text-sm mt-1 leading-relaxed">
              Einladungslink öffnen → in Element beitreten → verschlüsselte Verbindung steht.
            </p>
          ),
        },
        {
          step: 3,
          Icon: Mic,
          title: 'Diktat aufnehmen',
          body: (
            <p className="text-slate-400 text-sm mt-1 leading-relaxed">
              Sprachnachricht im Raum senden — Transkription und Protokoll folgen automatisch.
            </p>
          ),
        },
        {
          step: 4,
          Icon: FileCheck,
          title: 'Protokoll erhalten',
          body: (
            <p className="text-slate-400 text-sm mt-1 leading-relaxed">
              PDF-Link in Element oder unter Dokumente im Portal.
            </p>
          ),
        },
      ].map(({ step, Icon, title, body }) => (
        <div key={step} className={`${PORTAL_SHELL.ingestBar} p-4`}>
          <div className="flex gap-3">
            <div className="w-9 h-9 rounded-lg bg-white/[0.06] flex items-center justify-center flex-shrink-0">
              <Icon className="w-4 h-4 text-slate-400" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-white text-sm font-medium">{title}</h3>
              {body}
            </div>
          </div>
        </div>
      ))}

      <p className="text-slate-500 text-xs">
        Fragen?{' '}
        <button type="button" onClick={onOpenChat} className="text-blue-400 hover:text-blue-300 underline">
          ARKO fragen
        </button>
      </p>
    </div>
  );
};
